import { Link } from 'react-router-dom';
import { useState } from 'react';
import { useSEO } from '../../utils/seo';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';

export default function PhotoBoothPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://funtimephotobooths.com';
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  useSEO({
    title: 'Signature Photo Booth Rental San Diego | Fun Time Photobooths',
    description: 'Premium photo booth rentals in San Diego with unlimited prints, custom templates, and professional attendants. Perfect for weddings, corporate events, and celebrations.',
    keywords: 'photo booth rental San Diego, wedding photo booth, corporate photo booth, event photo booth, custom photo booth templates',
    canonical: `${siteUrl}/photo-booth`,
    ogTitle: 'Signature Photo Booth Rental San Diego | Fun Time Photobooths',
    ogDescription: 'Premium photo booth with unlimited prints, custom templates, and professional attendants for your special events.',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Service',
          name: 'Signature Photo Booth',
          description: 'Premium photo booth rentals with unlimited prints, custom templates, and professional attendants for weddings, corporate events, and celebrations in San Diego.',
          provider: {
            '@type': 'LocalBusiness',
            name: 'Fun Time Photobooths',
            telephone: '+14424160576',
            address: {
              '@type': 'PostalAddress',
              addressLocality: 'San Diego',
              addressRegion: 'CA',
              addressCountry: 'US',
            },
          },
          areaServed: {
            '@type': 'City',
            name: 'San Diego',
          },
          url: `${siteUrl}/photo-booth`,
        },
        {
          '@type': 'FAQPage',
          mainEntity: [
            {
              '@type': 'Question',
              name: 'What is included in the photo booth rental?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Our photo booth rental includes a professional photo booth setup with camera and lighting, unlimited high-quality prints, custom photo templates, fun props and accessories, a professional attendant, and access to an online digital gallery with all your photos.',
              },
            },
            {
              '@type': 'Question',
              name: 'Are there unlimited prints with the photo booth?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Yes! There are absolutely no limits on the number of photo sessions or prints. Your guests can come back as many times as they want throughout the event.',
              },
            },
            {
              '@type': 'Question',
              name: 'Can I get custom photo templates for my event?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Absolutely. We create personalized photo templates featuring your event details, logos, color schemes, and custom designs. Visit our custom templates page to see all the options available.',
              },
            },
            {
              '@type': 'Question',
              name: 'Do you provide a professional attendant with the photo booth?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Yes, a dedicated professional attendant is included with every rental. They manage the booth, help guests get the perfect shot, and ensure everything runs smoothly throughout your event.',
              },
            },
            {
              '@type': 'Question',
              name: 'Can guests access their photos after the event?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Yes! All photos are available through a private online digital gallery that you can share with all your guests for downloading and sharing anytime after the event.',
              },
            },
          ],
        },
      ],
    },
  });

  const galleryImages = [
    {
      url: 'https://static.readdy.ai/image/4e6cb48aacd8b48acab6684eff653bcc/4232dc6dcf725cfb6d83db801a927370.jpeg',
      alt: 'Photo Booth at Wedding Event',
    },
    {
      url: 'https://static.readdy.ai/image/4e6cb48aacd8b48acab6684eff653bcc/10dccba61776622dfb161356cd36d338.jpeg',
      alt: 'Photo Booth at Corporate Event',
    },
    {
      url: 'https://static.readdy.ai/image/4e6cb48aacd8b48acab6684eff653bcc/c03936f68d59e095eb3bff43a9ee34db.jpeg',
      alt: 'Photo Booth at Birthday Party',
    },
    {
      url: 'https://static.readdy.ai/image/4e6cb48aacd8b48acab6684eff653bcc/11c5d6bcb38554681af1d07142c6edd7.jpeg',
      alt: 'Photo Booth at Gala Event',
    },
  ];

  return (
    <div className="min-h-screen">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative h-[70vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <iframe
            src="https://www.youtube.com/embed/rMtte4inipk?autoplay=1&mute=1&loop=1&playlist=rMtte4inipk&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1"
            className="absolute top-1/2 left-1/2 w-[177.77777778vh] h-[56.25vw] min-h-full min-w-full -translate-x-1/2 -translate-y-1/2"
            allow="autoplay; encrypted-media"
            title="Photo Booth Background Video"
            style={{ border: 'none', pointerEvents: 'none' }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Signature Photo Booth
          </h1>
          <p className="text-xl md:text-2xl text-white">
            Classic Luxury, Timeless Memories
          </p>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Premium Photo Booth Experience
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Our signature photo booth combines professional quality with fun, creating memories your guests will treasure forever
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-gray-50 rounded-xl p-8">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-camera-3-line text-3xl text-orange-500"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                High-Resolution Prints
              </h3>
              <p className="text-gray-600">
                Professional-quality photo prints that your guests can take home as instant keepsakes.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-infinity-line text-3xl text-orange-500"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Unlimited Sessions
              </h3>
              <p className="text-gray-600">
                No limits on the number of photo sessions – your guests can come back as many times as they want.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-layout-masonry-line text-3xl text-orange-500"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Custom Templates
              </h3>
              <p className="text-gray-600 mb-4">
                Personalized photo templates featuring your event details, logos, or custom designs.
              </p>
              <Link
                to="/custom-templates"
                className="text-orange-500 hover:text-orange-600 font-medium transition-colors cursor-pointer inline-flex items-center gap-1"
              >
                Learn More <i className="ri-arrow-right-line"></i>
              </Link>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-emotion-happy-line text-3xl text-orange-500"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Fun Props & Backdrops
              </h3>
              <p className="text-gray-600">
                Extensive collection of props and customizable backdrops to match your event theme.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-gallery-line text-3xl text-orange-500"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Digital Gallery
              </h3>
              <p className="text-gray-600">
                Access all photos online through a private digital gallery that you can share with guests.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-user-smile-line text-3xl text-orange-500"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Professional Staff
              </h3>
              <p className="text-gray-600">
                Friendly attendants ensure everything runs smoothly and help guests get the perfect shot.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Our Photo Booth in Action
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              See how our signature photo booth brings joy and creates lasting memories at events
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {galleryImages.map((image, index) => (
              <div
                key={index}
                className="relative overflow-hidden rounded-lg cursor-pointer group"
                onClick={() => setSelectedImage(image.url)}
              >
                <div className="w-full h-64">
                  <img
                    src={image.url}
                    alt={image.alt}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <button
            className="absolute top-6 right-6 w-12 h-12 flex items-center justify-center text-white hover:bg-white/10 rounded-full transition-colors cursor-pointer"
            onClick={() => setSelectedImage(null)}
          >
            <i className="ri-close-line text-3xl"></i>
          </button>
          <div className="max-w-6xl w-full h-full flex items-center justify-center">
            <img
              src={selectedImage}
              alt="Gallery image"
              className="max-w-full max-h-full object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}

      {/* Package Options */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              What's Included
            </h2>
          </div>

          <div className="max-w-3xl mx-auto bg-white rounded-xl shadow-lg p-8">
            <ul className="space-y-4">
              <li className="flex items-start gap-4">
                <i className="ri-check-double-line text-2xl text-orange-500 mt-1"></i>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Professional Photo Booth Setup</h4>
                  <p className="text-gray-600">Complete booth with professional camera and lighting equipment</p>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <i className="ri-check-double-line text-2xl text-orange-500 mt-1"></i>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Unlimited High-Quality Prints</h4>
                  <p className="text-gray-600">As many prints as your guests want throughout your event</p>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <i className="ri-check-double-line text-2xl text-orange-500 mt-1"></i>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Custom Photo Templates</h4>
                  <p className="text-gray-600">Personalized designs featuring your event information</p>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <i className="ri-check-double-line text-2xl text-orange-500 mt-1"></i>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Props & Accessories</h4>
                  <p className="text-gray-600">Fun selection of props to enhance the photo experience</p>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <i className="ri-check-double-line text-2xl text-orange-500 mt-1"></i>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Professional Attendant</h4>
                  <p className="text-gray-600">Dedicated staff member for the duration of your event</p>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <i className="ri-check-double-line text-2xl text-orange-500 mt-1"></i>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Online Gallery Access</h4>
                  <p className="text-gray-600">Digital copies of all photos available for download and sharing</p>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-orange-500 to-orange-600">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Book Your Photo Booth Today
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Let's create unforgettable memories at your next event. Contact us for availability and pricing.
          </p>
          <Link
            to="/contact"
            className="inline-block px-8 py-4 bg-white text-orange-500 rounded-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
          >
            Request a Quote
          </Link>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
}
